import { EventsManager } from '@game-libs/events-manager';
import { AudioManager } from '@game-libs/audio-manager';
import { Engine } from 'excalibur';
import { GUIActions, UpgradeActions } from '../types/game-types';
import { getRandomThing } from '@game-libs/exc-libs/src/helpers';
import { GameEvents, MenuEvents } from '@game-libs/models';
import { DungeonEvents, GameplayEvents } from '../types/game-events';
import { TimedEventsOrchestrator } from '@game-libs/timed-events-orchestrator';
import { GlobalGameStore } from '@game-libs/global-game-store';
import { SuperSaveSystem } from '@game-libs/super-save-system';
import { resetAllStores } from '../store/zustand';
import { setClearingSave } from '../store/save-state';
import { SFXList } from '.';


export const initActionListeners = (game: Engine) => {
  const { playSFX, playSFXNow } = AudioManager();
  const { state } = GlobalGameStore();
  const { on } = EventsManager();

  const swordSFXList: SFXList = [
    { value: 'sword_1', probability: 25 },
    { value: 'sword_2', probability: 25 },
    { value: 'sword_3', probability: 20 },
    { value: 'sword_4', probability: 15 },
    { value: 'sword_5', probability: 10 },
    { value: 'sword_6', probability: 5 },
  ];

  const coinSFXList: SFXList = [
    { value: 'coin_1', probability: 40 },
    { value: 'coin_2', probability: 30 },
    { value: 'coin_3', probability: 30 },
  ];

  const hoverSFXList: SFXList = [
    { value: 'hover_1', probability: 34 },
    { value: 'hover_2', probability: 33 },
    { value: 'hover_3', probability: 33 },
  ];

  const criticalSFXList: SFXList = [
    { value: 'haha_1', probability: 8.3 },
    { value: 'haha_2', probability: 8.3 },
    { value: 'haha_3', probability: 8.3 },
    { value: 'huh_1', probability: 8.3 },
    { value: 'huh_2', probability: 8.3 },
    { value: 'huh_3', probability: 7.3 },
    { value: 'huh_4', probability: 7.3 },
    { value: 'yeah_1', probability: 8.3 },
    { value: 'yeah_2', probability: 8.3 },
    { value: 'yeah_3', probability: 7.3 },
    { value: 'yeah_4', probability: 7.3 },
    { value: 'take_that_1', probability: 12.3 },
  ];

  on(GUIActions.CLICK_DRAGON, ({ damage }) => {
    // Respect the audio setting that mutes per-click sword swooshes.
    const playSwordSwooshSounds = state.globalVariables.value.swordSwooshSounds ?? true;
    if (!playSwordSwooshSounds) return;

    playSFX(getRandomThing<string>(swordSFXList), 0.75);
  });

  on(DungeonEvents.ATTACK_ENEMY, () => {
    playSFX(getRandomThing<string>(swordSFXList), 0.75);
  });

  on(GUIActions.INIT_GAME, () => {
    if (state.currentLevel.value === 'dummy') return;
    playSFXNow('roar_2');
  });

  on(GUIActions.WIN_GAME, () => {
    playSFXNow('roar_1');
  });

  on(GUIActions.CLICK_BIRD, () => {
    playSFXNow('bird_coin');
  });

  on(GameplayEvents.CRITICAL_HIT, () => {
    // Same toggle also mutes critical-hit voice lines on click.
    const playSwordSwooshSounds = state.globalVariables.value.swordSwooshSounds ?? true;
    if (!playSwordSwooshSounds) return;

    playSFXNow(getRandomThing<string>(criticalSFXList), 0.8);
  });

  on(GUIActions.BUY_GENERATOR, (action) => {
    if (action.amountToBuy > 1) {
      playSFX('coin_many');
      return;
    }

    playSFX(getRandomThing<string>(coinSFXList));
  });

  on(GUIActions.HOVER_ITEM, () => {
    playSFX(getRandomThing<string>(hoverSFXList));
  });

  on(UpgradeActions.BUY_UPGRADE, (action) => {
    // EA_buyItem('gold', action.cost, 'upgrade', action.id, action.amountToBuy);

    if (action.amountToBuy > 1) {
      playSFX('coin_many');
      return;
    }

    playSFX(getRandomThing<string>(coinSFXList));
  });

  on(GUIActions.ENOUGH_MANA, (action) => {
    playSFXNow('mana_fill');
  });

  on(GUIActions.NOT_ENOUGH_MANA, (action) => {
    playSFXNow('mana_out', 1.3);
  });

  on(GameEvents.START_DIALOG, () => {
    playSFX('start_dialog');
  });

  on(GameplayEvents.POISON_ATTACK_DRAGON, () => {
    playSFXNow('poisonHit');
  });

  on(GameplayEvents.LIGHTNING_DAMAGE_DRAGON, () => {
    playSFXNow('lightningHit', 0.8);
  });

  on(GUIActions.TOGGLE_BUY_MAX, ({ buyMax }) => {
    playSFX(MenuEvents.SELECT);
  });

  on(GUIActions.TOGGLE_UI, () => {
    playSFX(MenuEvents.SELECT);
  });

  on(GUIActions.CHEATS, ({ property, value }) => {
    playSFX(MenuEvents.SELECT);
  });

  on(GUIActions.CLEAR_SAVE, () => {
    // Prevent saveState (which uses setTimeout) from overwriting our clear before reload
    setClearingSave(true);
    resetAllStores();
    SuperSaveSystem().clear().then(() => {
      window.location.reload();
    });
  });

  on(GameplayEvents.POPE_HEAL_DRAGON, () => {
    playSFX('heal');
  });

  // Timed Events listeners
  on(GameplayEvents.DRAGON_ROAR, () => {
    const { unpauseGroup } = TimedEventsOrchestrator();

    setTimeout(() => {
      unpauseGroup('DragonAttack');
    }, 5000);
  });

  on('dad_dragon_reveal', () => {
    playSFXNow('crash', 1.3);
  });
};
